import React, { useEffect, useState } from 'react';
import { X, Send, FileText, Info, Loader2, Mail, AlertCircle, Building2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const fmtKB = (n) => `${Math.ceil((n || 0) / 1024)} KB`;
const fmtMB = (n) => `${((n || 0) / (1024 * 1024)).toFixed(2)} MB`;

const ForwardToCollegeModal = ({ student, onClose, onSent }) => {
  const { toast } = useToast();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ override_email: '', subject_override: '', extra_notes: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/students/${student.id}/forward-preview`);
        setPreview(data);
        setForm((f) => ({ ...f, subject_override: data.default_subject }));
      } catch (e) {
        toast({ title: 'Failed to load preview', description: e?.response?.data?.detail || 'Error' });
        onClose();
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [student.id]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const to = form.override_email || preview?.college?.representative_email;
    if (!to) {
      toast({ title: 'No recipient', description: 'Provide a college rep email or set one on the college.' });
      return;
    }
    setBusy(true);
    try {
      const payload = {
        override_email: form.override_email || null,
        extra_notes: form.extra_notes || '',
        subject_override: form.subject_override || null,
      };
      const { data } = await api.post(`/admin/students/${student.id}/forward-to-college`, payload);
      toast({
        title: 'Forwarded successfully',
        description: `${data.attached} document(s) sent to ${data.to}${data.skipped?.length ? ` · ${data.skipped.length} skipped (size)` : ''}`,
      });
      onSent?.();
    } catch (err) {
      toast({ title: 'Forward failed', description: err?.response?.data?.detail || 'Email send failed' });
    } finally { setBusy(false); }
  };

  const recipientDisplay = form.override_email || preview?.college?.representative_email || '';
  const overLimit = (preview?.total_size || 0) > (preview?.size_limit || 0);
  const hasNoRep = preview && !preview.college?.representative_email && !form.override_email;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-white rounded-2xl shadow-2xl pointer-events-auto">
          <div className="sticky top-0 bg-white z-10 border-b border-[#0B5E2A]/10 px-6 py-4 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#6b7e6d] font-semibold">Forward to College</div>
              <h3 className="font-display text-xl text-[#0B5E2A]">{student.full_name}</h3>
            </div>
            <button type="button" onClick={onClose} className="p-2 rounded hover:bg-[#f0f6ee]">
              <X className="h-5 w-5 pointer-events-none" />
            </button>
          </div>

          {loading ? (
            <div className="p-12 text-center text-[#6b7e6d]"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
          ) : (
            <form onSubmit={submit} className="p-6 space-y-5">
              {/* College context */}
              {preview?.college?.name ? (
                <div className="flex items-start gap-2.5 bg-[#E1F3E4] border border-[#0B5E2A]/20 rounded-lg p-3 text-sm">
                  <Building2 className="h-4 w-4 mt-0.5 text-[#0B5E2A] shrink-0" />
                  <div>
                    <div className="font-semibold text-[#0B5E2A]">{preview.college.name}</div>
                    <div className="text-xs text-[#4b5e4d] mt-0.5">
                      {preview.student.assigned_program_name && <span>Program: {preview.student.assigned_program_name}</span>}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2.5 bg-[#FEF4DB] border border-[#F0B93C]/40 rounded-lg p-3 text-sm text-[#8a5e00]">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>No college assigned to this student. Please provide a recipient email below.</div>
                </div>
              )}

              {/* Recipient */}
              <div>
                <label className="block text-sm font-medium text-[#2e4030] mb-1.5">To *</label>
                <div className="flex items-center gap-2 bg-[#f6faf3] border border-[#0B5E2A]/20 rounded-md px-3 py-2">
                  <Mail className="h-4 w-4 text-[#6b7e6d]" />
                  <input
                    type="email"
                    placeholder={preview?.college?.representative_email || 'recipient@college.edu'}
                    value={form.override_email}
                    onChange={onChange('override_email')}
                    className="flex-1 bg-transparent text-sm focus:outline-none"
                  />
                  {preview?.college?.representative_email && !form.override_email && (
                    <span className="text-xs text-[#6b7e6d]">(pre-filled)</span>
                  )}
                </div>
                {recipientDisplay && (
                  <div className="mt-1 text-xs text-[#4b5e4d]">Will send to <span className="font-semibold text-[#0B5E2A]">{recipientDisplay}</span></div>
                )}
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-[#2e4030] mb-1.5">Subject</label>
                <input value={form.subject_override} onChange={onChange('subject_override')} className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30" />
              </div>

              {/* Extra Notes */}
              <div>
                <label className="block text-sm font-medium text-[#2e4030] mb-1.5">Additional Notes for College</label>
                <textarea value={form.extra_notes} onChange={onChange('extra_notes')} rows={4} placeholder="Optional context to include in the email" className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30 resize-y" />
              </div>

              {/* Documents preview */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-[#2e4030]">Attachments ({preview?.documents?.length || 0})</label>
                  <span className={`text-xs font-semibold ${overLimit ? 'text-[#9b1c1c]' : 'text-[#6b7e6d]'}`}>
                    {fmtMB(preview?.total_size)} / 9.00 MB
                  </span>
                </div>
                {(preview?.documents || []).length === 0 ? (
                  <div className="text-sm text-[#6b7e6d] italic bg-[#f6faf3] rounded p-3">No documents uploaded for this student.</div>
                ) : (
                  <ul className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
                    {(preview.documents || []).map((d, i) => (
                      <li key={i} className="flex items-center gap-2.5 bg-[#f6faf3] border border-[#0B5E2A]/10 rounded-md px-3 py-2 text-sm">
                        <FileText className="h-3.5 w-3.5 brand-green shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate font-medium">{d.name}</div>
                          <div className="text-xs text-[#6b7e6d]">{d.doc_type} · {fmtKB(d.size)}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {overLimit && (
                  <div className="mt-2 flex items-start gap-2 bg-[#FEF4DB] border border-[#F0B93C]/40 rounded-md p-2.5 text-xs text-[#8a5e00]">
                    <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>Total size exceeds the 9MB limit. Some documents will be skipped (you'll see which ones in the result).</span>
                  </div>
                )}
              </div>

              <div className="bg-[#DEE9FA] text-[#1e40af] rounded-lg p-3 text-sm flex items-start gap-2">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <span>A formatted email with the student's profile table and all attachments will be sent via Resend. The action will be logged in <strong>Forward History</strong>.</span>
              </div>

              <div className="flex gap-2 pt-2 border-t border-[#0B5E2A]/10">
                <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-md border border-[#0B5E2A]/25 text-[#0B5E2A] font-semibold hover:bg-[#f0f6ee] transition-colors">Cancel</button>
                <button type="submit" disabled={busy || hasNoRep} className="flex-1 btn-green px-5 py-2.5 rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>) : (<><Send className="h-4 w-4 pointer-events-none" /> Forward to College</>)}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default ForwardToCollegeModal;
